import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigation, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { Button } from "@/components/ui/button";

const LIVE = ["en_route", "arrived", "in_progress"];
export const FOLLOW_COPY = { en_route: "YOUR DRIVER IS ON THE WAY", arrived: "YOUR DRIVER HAS ARRIVED", in_progress: "RIDE IN PROGRESS", assigned: "DRIVER ASSIGNED" };

export const useFollow = (ride) => useQuery({
  queryKey: ["follow", ride?.id],
  queryFn: () => api.get(`/rides/${ride.id}/follow`).then((r) => r.data),
  enabled: !!ride?.driver_id && ["assigned", ...LIVE].includes(ride?.status),
  refetchInterval: 15000,
});

export function FollowBanner({ follow }) {
  if (!follow) return null;
  return (
    <div className="rounded-2xl border border-neon-cyan/40 bg-neon-cyan/10 p-4" data-testid="follow-driver-panel">
      <p className="flex items-center gap-2 font-display text-sm font-bold tracking-wide"><Navigation className="h-4 w-4 text-neon-cyan" /> FOLLOW MY DRIVER</p>
      <p className="mt-1 text-lg font-bold" data-testid="follow-status">{FOLLOW_COPY[follow.status] || follow.status}</p>
      {follow.sharing ? (
        <p className="text-sm" data-testid="follow-eta">{follow.eta_minutes ? `ETA: ${follow.eta_minutes} minute${follow.eta_minutes === 1 ? "" : "s"}${follow.eta_miles != null ? ` · ${follow.eta_miles} mi away` : ""}` : "Locating your driver…"}</p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="follow-off">Live location appears once your driver is on the way.</p>
      )}
      {follow.vehicle && <p className="mt-1 text-xs text-muted-foreground">{follow.driver_name} · {follow.vehicle}</p>}
    </div>
  );
}

export function ShareRideButton({ rideId }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rides/${rideId}/share`);
      const url = `${window.location.origin}${data.path}`;
      if (navigator.share) await navigator.share({ title: "Follow my Scoopz ride", url }).catch(() => {});
      else await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Tracking link ready", { description: `${url} · expires ${when(data.expires_at)}` });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" className="rounded-full" onClick={share} disabled={busy} data-testid={`share-ride-button-${rideId}`}>
      <Share2 className="mr-1.5 h-4 w-4" />Share my ride
    </Button>
  );
}

/** Driver side: send GPS only while this assigned ride is live; stops automatically when the ride ends. */
export function useShareLocation(ride) {
  const [on, setOn] = useState(false);
  const last = useRef(0);
  const live = !!ride && LIVE.includes(ride.status) && ride.location_sharing;
  useEffect(() => {
    if (!live || !navigator.geolocation) {
      setOn(false);
      return undefined;
    }
    const id = navigator.geolocation.watchPosition((p) => {
      if (Date.now() - last.current < 15000) return;
      last.current = Date.now();
      api.post(`/driver/rides/${ride.id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).then(() => setOn(true)).catch(() => setOn(false));
    }, () => setOn(false), { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [live, ride?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return on;
}

export const SharingBadge = ({ on }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${on ? "bg-neon-lime/15 text-neon-lime" : "bg-muted text-muted-foreground"}`} data-testid="location-sharing-badge">
    <span className={`h-2 w-2 rounded-full ${on ? "bg-neon-lime" : "bg-muted-foreground"}`} />LOCATION SHARING {on ? "ON" : "OFF"}
  </span>
);
